import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Globe, Volume2, BookOpen, TrendingUp, Users, Languages } from 'lucide-react';
import { publicAPI } from '../services/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_users: 0,
    total_languages: 9,
    total_phrases_learned: 0,
    program_enrollments: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await publicAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k+`;
    }
    return num > 0 ? `${num}+` : '0';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm fixed w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Globe className="h-8 w-8 text-amber-600" />
              <span className="text-xl font-bold text-slate-900">NomadLinguist</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/login')}>
                Login
              </Button>
              <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => navigate('/register')}>
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Speak with Confidence
            <span className="block text-amber-600 mt-2">While Traveling</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
            Master essential conversations in 9 languages. Learn real phrases travelers need, 
            with native pronunciation and instant translation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              className="bg-amber-600 hover:bg-amber-700 text-lg px-8 py-6"
              onClick={() => navigate('/register')}
              data-testid="hero-get-started-btn"
            >
              Start Learning Free
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="text-lg px-8 py-6 border-2"
              onClick={() => navigate('/program')}
              data-testid="hero-browse-btn"
            >
              Browse Sample Lessons
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-16">
            Everything You Need to Communicate
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                <Volume2 className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Native Pronunciation</h3>
              <p className="text-slate-600">
                Listen to authentic pronunciations with male and female voice options. 
                Learn how locals really speak.
              </p>
            </div>

            <div className="p-6 rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center mb-4">
                <BookOpen className="h-6 w-6 text-sky-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Real Travel Phrases</h3>
              <p className="text-slate-600">
                No grammar drills. Just practical conversations you'll actually use at restaurants, 
                hotels, and streets.
              </p>
            </div>

            <div className="p-6 rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Track Your Progress</h3>
              <p className="text-slate-600">
                See your learning journey with clear progress tracking. 
                Resume right where you left off.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Languages Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Languages className="h-12 w-12 text-amber-600 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {stats.total_languages} Languages, One Platform
            </h2>
            <p className="text-xl text-slate-600">
              From European destinations to Indian adventures
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {['Spanish', 'French', 'German', 'Italian', 'Hindi', 'Tamil', 'Kannada', 'Telugu', 'Malayalam'].map((lang) => (
              <div key={lang} className="p-4 bg-white rounded-lg border-2 border-slate-200 hover:border-amber-400 hover:shadow-md transition-all text-center">
                <p className="font-semibold text-slate-900">{lang}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof - Real Stats */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto text-center">
          <Users className="h-12 w-12 text-amber-600 mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
            Join {formatNumber(stats.total_users)} Confident Travelers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-12">
            <div className="p-6" data-testid="stat-users">
              <p className="text-4xl font-bold text-amber-600 mb-2">{formatNumber(stats.total_users)}</p>
              <p className="text-slate-600">Registered Learners</p>
            </div>
            <div className="p-6" data-testid="stat-languages">
              <p className="text-4xl font-bold text-amber-600 mb-2">{stats.total_languages}</p>
              <p className="text-slate-600">Languages Supported</p>
            </div>
            <div className="p-6" data-testid="stat-phrases">
              <p className="text-4xl font-bold text-amber-600 mb-2">{formatNumber(stats.total_phrases_learned)}</p>
              <p className="text-slate-600">Phrases Learned</p>
            </div>
            <div className="p-6" data-testid="stat-program">
              <p className="text-4xl font-bold text-amber-600 mb-2">{formatNumber(stats.program_enrollments)}</p>
              <p className="text-slate-600">Program Enrollments</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Start Speaking Today
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            No credit card required. Free to use. Pay what you feel it's worth.
          </p>
          <Button 
            size="lg" 
            className="bg-amber-600 hover:bg-amber-700 text-lg px-8 py-6"
            onClick={() => navigate('/register')}
            data-testid="cta-create-account-btn"
          >
            Create Free Account
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Globe className="h-6 w-6 text-amber-600" />
            <span className="text-lg font-semibold">NomadLinguist</span>
          </div>
          <p className="text-slate-400 mb-4">
            Simple, practical language learning for travelers
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
